import React from "react";
import { Input } from "../components/ui/input";

/**
 * NumberInput: accepts both "." and "," as decimal separators.
 * Displays value with "," as decimal separator (Italian locale).
 * Stores and emits onChange with a JS number.
 *
 * KEY BEHAVIOR (since iter46):
 *   When the user types ".", it is INSTANTLY converted to ",".
 *   So both "1.5" and "1,5" become "1,5" visually, parsed as 1.5 numerically.
 *   This avoids the old ambiguity where "1.5" was wrongly stripped to "15"
 *   (treating dot as thousand separator).
 */
const NumberInput = React.forwardRef(
  ({ value, onChange, step, min, max, placeholder, className, disabled, "data-testid": testId, ...rest }, ref) => {
    const [text, setText] = React.useState(() => formatItalian(value));

    React.useEffect(() => {
      // Re-sync from parent only when:
      //   - the text is NOT currently empty (user is not actively clearing the field), AND
      //   - the parent number differs from what the current text would parse to
      // This avoids the "leading 0" bug where clearing → onChange(0) → re-sync paints "0" back.
      if (text === "") return;
      const currentParsed = parseItalian(text);
      const incoming = Number(value);
      if (isNaN(currentParsed) || Math.abs(currentParsed - incoming) > 1e-9) {
        setText(formatItalian(value));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleFocus = (e) => {
      // UX polish: auto-select on focus so a single keystroke replaces the value cleanly.
      try { e.target.select(); } catch (_) { /* noop */ }
    };

    const handleChange = (e) => {
      // INSTANT dot → comma conversion as user types
      let raw = e.target.value.replace(/\./g, ",");
      if (raw !== "" && !/^-?\d*,?\d*$/.test(raw)) return;
      setText(raw);
      if (raw === "" || raw === "-" || raw === ",") {
        // Do NOT emit onChange(0) here — leave parent state untouched until the user
        // commits a real value. This prevents the "leading 0" bug after clearing.
        return;
      }
      const num = parseItalian(raw);
      if (!isNaN(num)) {
        onChange?.({ target: { value: num } });
      }
    };

    const handleBlur = () => {
      // On blur, if text is empty or invalid, commit 0 and reset display.
      const parsed = parseItalian(text);
      if (text === "" || isNaN(parsed)) {
        onChange?.({ target: { value: 0 } });
        setText(formatItalian(0));
      } else {
        setText(formatItalian(parsed));
      }
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={text}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        data-testid={testId}
        {...rest}
      />
    );
  },
);

function formatItalian(num) {
  if (num === null || num === undefined || num === "") return "";
  const n = Number(num);
  if (isNaN(n)) return "";
  return String(n).replace(".", ",");
}

function parseItalian(s) {
  if (s === null || s === undefined || s === "") return NaN;
  return parseFloat(String(s).replace(",", "."));
}

NumberInput.displayName = "NumberInput";
export default NumberInput;
